"use client";

import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useState, useEffect } from "react";

import { handleDetailInfoModalOpened, handleSetActiveImageIndex } from "@/src/store/listingDetail";
import { FLOOR_TYPES } from "@/src/utils/constants/project-detail";

import ZoomIcon from "@/src/assets/svg/zoom_ikon.svg";

import { LazyImage } from "../../common/lazy-image/LazyImage";
import Table from "@/src/components/project-radar-landing-page/table";
import ContactFormModal from "@/src/components/project-detail/contact-form-modal";

import styles from "./styles.module.css";

const HousingTypes = ({ unitTypes, updateFormattedDataImages, formObject, salesStatus }) => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState(null);

  if (unitTypes.length === 0) return null;

  const enhancedUnitTypes = unitTypes.map((item, index) => ({ ...item, index }));

  const handleFormData = (roomType, area) => {
    setFormData(`${roomType}, ${area} m²`);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setFormData(null);
  };

  const setActiveImageIndex = (activeImageIndex) => dispatch(handleSetActiveImageIndex(activeImageIndex));

  const handleOpenDetailInfoModal = (activeTabName, activeImageIndex) => {
    updateFormattedDataImages("housing-types");

    if (typeof window !== "undefined") {
      window.history.replaceState(
        null,
        "",
        `${window.location.pathname}#?unite-tipi-${enhancedUnitTypes[activeImageIndex]?.area}-${enhancedUnitTypes[activeImageIndex]?.roomCount}`
      );
    }

    dispatch(handleDetailInfoModalOpened(activeTabName));
    if (activeTabName === "picture") setActiveImageIndex(activeImageIndex);
  };

  const columns = [
    {
      key: "housingPlan",
      title: "Konut Planı",
      dataIndex: "name",
      render: (name, record) => (
        <div className={styles.housingPlan}>
          <LazyImage
            src={record.url || "/assets/images/no_photo_big.jpg"}
            alt={name}
            className={styles.housingPlanImage}
          />
          <div className={styles.zoomIcon} onClick={() => handleOpenDetailInfoModal("picture", record.index)}>
            <ZoomIcon />
          </div>
        </div>
      ),
    },
    {
      key: "floorType",
      title: "Kat Tipi",
      dataIndex: "floor",
      render: (floor) => (
        <span className={styles.columnDetail}>{FLOOR_TYPES.find((item) => item.value === floor)?.label}</span>
      ),
    },
    {
      key: "roomType",
      title: "Oda Tipi",
      dataIndex: "roomType",
      render: (roomType) => <span className={styles.columnDetail}>{roomType}</span>,
    },
    {
      key: "size",
      title: "Büyüklük",
      dataIndex: "area",
      render: (area) => <span className={styles.columnDetail}>{area} m²</span>,
    },
    {
      key: "price",
      title: "Fiyat",
      dataIndex: "flatPrice",
      render: (flatPrice, record) => (
        <div className={styles.priceContainer}>
          <span className={styles.price}>
            {flatPrice
              ? `${new Intl.NumberFormat("tr-TR").format(flatPrice)}${record.currency ? ` ${record.currency}` : " TL"}`
              : "Fiyat belirtilmemiştir"}
          </span>
          {salesStatus !== "FINISHED" && (
            <span className={styles.infoButton} onClick={() => handleFormData(record.roomType, record.area)}>
              Bilgi Al
            </span>
          )}
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (window.location.hash.length > 0) {
      const hashParts = window.location.hash.slice(13).split("-");
      const index = enhancedUnitTypes.findIndex(
        (unitType) => unitType?.area === Number(hashParts[0]) && unitType?.roomCount === hashParts[1]
      );

      if (index !== -1) {
        handleOpenDetailInfoModal("picture", index);
      }
    }
  }, []);

  return (
    <>
      <div className={styles.wrapper}>
        <b className={styles.title}>Konut Tipleri</b>
        <Table columns={columns} data={enhancedUnitTypes} isMoreVisible={false} />
      </div>
      <ContactFormModal
        isOpen={isOpen}
        handleClose={handleClose}
        currentCompany={formObject}
        projectDetailFormData={formData}
      />
    </>
  );
};

HousingTypes.propTypes = {
  unitTypes: PropTypes.array.isRequired,
  updateFormattedDataImages: PropTypes.func,
  formObject: PropTypes.object,
  salesStatus: PropTypes.string,
};

export default HousingTypes;
